import { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, ClipboardList, UtensilsCrossed, Droplets } from "lucide-react";
import { Kicker, LineReveal, Reveal } from "@/components/Reveal";
import InquiryModal from "@/components/InquiryModal";
import Marquee from "@/components/Marquee";

const CARE_ICONS = {
  clipboard: ClipboardList,
  utensils: UtensilsCrossed,
  droplets: Droplets,
};

export default function DivisionPage({ division }) {
  const [open, setOpen] = useState(false);
  const [searchParams] = useSearchParams();
  const heroRef = useRef(null);

  useEffect(() => {
    if (searchParams.get("cotizar") === "1") setOpen(true);
  }, [searchParams]);
  const { scrollYProgress } = useScroll({
    target: heroRef,
    offset: ["start start", "end start"],
  });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "20%"]);
  const bgScale = useTransform(scrollYProgress, [0, 1], [1.05, 1.22]);

  return (
    <main className="bg-void" data-testid={`page-${division.slug}`}>
      <section
        ref={heroRef}
        className="noise-overlay relative flex min-h-[92vh] items-end overflow-hidden"
      >
        <motion.div className="absolute inset-0" style={{ y: bgY, scale: bgScale }}>
          <img
            src={division.hero}
            alt={division.name}
            className="h-full w-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-void via-void/70 to-void/30" />
        </motion.div>

        <div className="relative z-10 mx-auto w-full max-w-7xl px-4 pb-24 pt-44 sm:px-6 lg:px-8">
          <Reveal>
            <Kicker color={division.accent}>
              {division.index} — {division.tag}
            </Kicker>
          </Reveal>
          <h1 className="mt-6 font-serif text-4xl font-bold leading-[1.05] tracking-tight text-slate-100 sm:text-5xl lg:text-7xl">
            <LineReveal lines={[division.name]} />
          </h1>
          <Reveal delay={0.35}>
            <p className="mt-6 max-w-xl font-serif text-xl italic text-gold-light/90 sm:text-2xl">
              {division.tagline}
            </p>
          </Reveal>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-8 lg:py-28">
        <div className="grid gap-12 lg:grid-cols-[1fr_1.4fr] lg:gap-20">
          <Reveal>
            <Kicker color={division.accent}>Quiénes somos</Kicker>
            <h2 className="mt-4 font-serif text-2xl font-semibold tracking-tight text-slate-100 sm:text-3xl lg:text-4xl">
              Un estándar que no se negocia.
            </h2>
          </Reveal>
          <Reveal delay={0.15}>
            <p className="text-base font-light leading-relaxed text-slate-300 sm:text-lg">
              {division.description}
            </p>
          </Reveal>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 pb-20 sm:px-6 lg:px-8 lg:pb-28" data-testid={`${division.slug}-services`}>
        <Reveal>
          <Kicker color={division.accent}>Capacidades</Kicker>
        </Reveal>
        <div className="mt-8 grid gap-px bg-line sm:grid-cols-2">
          {division.services.map((s, i) => (
            <Reveal key={s.title} delay={i * 0.08} className="bg-void">
              <div className="group h-full border border-transparent p-8 transition-colors duration-500 hover:bg-ink lg:p-10">
                <span
                  className="font-mono text-xs tracking-[0.28em]"
                  style={{ color: division.accent }}
                >
                  {String(i + 1).padStart(2, "0")}
                </span>
                <h3 className="mt-4 text-xl font-medium tracking-tight text-slate-100 sm:text-2xl">
                  {s.title}
                </h3>
                <p className="mt-3 text-sm font-light leading-relaxed text-slate-400">
                  {s.desc}
                </p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {division.careSection && (
        <section className="border-t border-line bg-[#0A0E14]" data-testid={`${division.slug}-care`}>
          <div className="mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-8 lg:py-28">
            <Reveal>
              <Kicker color={division.accent}>{division.careSection.kicker}</Kicker>
              <h2 className="mt-4 max-w-3xl font-serif text-2xl font-semibold tracking-tight text-slate-100 sm:text-3xl lg:text-4xl">
                {division.careSection.title}
              </h2>
              <p className="mt-4 max-w-xl text-sm font-light text-slate-400 sm:text-base">
                {division.careSection.intro}
              </p>
            </Reveal>
            <div className="mt-12 grid gap-10 lg:grid-cols-2 lg:gap-16">
              <Reveal>
                <figure>
                  <div className="clip-frame group relative aspect-[4/3] overflow-hidden bg-ink">
                    <img
                      src={division.careSection.image}
                      alt={division.careSection.title}
                      loading="lazy"
                      className="h-full w-full object-cover brightness-[0.8] saturate-[0.85] transition-all duration-700 ease-out group-hover:scale-105 group-hover:brightness-100 group-hover:saturate-100"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-void/70 via-void/25 to-void/10 transition-opacity duration-500 group-hover:opacity-30" />
                  </div>
                  <figcaption className="mt-4">
                    <p
                      className="font-mono text-[10px] uppercase tracking-[0.24em]"
                      style={{ color: division.accent }}
                    >
                      Nutrición de alto rendimiento
                    </p>
                    <p className="mt-1 text-sm text-slate-300">
                      {division.careSection.imageCaption}
                    </p>
                  </figcaption>
                </figure>
              </Reveal>
              <div>
                {division.careSection.pillars.map((pillar, i) => {
                  const Icon = CARE_ICONS[pillar.icon];
                  return (
                    <Reveal key={pillar.title} delay={i * 0.08}>
                      <div className="group flex gap-6 border-t border-line py-8 transition-colors duration-500 first:border-t-0 first:pt-0 lg:py-10 lg:first:pt-0">
                        <Icon
                          className="mt-1 h-7 w-7 shrink-0 transition-transform duration-500 group-hover:-translate-y-1"
                          style={{ color: division.accent }}
                        />
                        <div>
                          <h3 className="text-lg font-medium tracking-tight text-slate-100 sm:text-xl">
                            {pillar.title}
                          </h3>
                          <p className="mt-2 text-sm font-light leading-relaxed text-slate-400">
                            {pillar.desc}
                          </p>
                        </div>
                      </div>
                    </Reveal>
                  );
                })}
              </div>
            </div>
          </div>
        </section>
      )}

      {division.trackRecord && (
        <section className="border-t border-line bg-[#0A0E14]" data-testid={`${division.slug}-track-record`}>
          <div className="mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-8 lg:py-28">
            <Reveal>
              <Kicker color={division.accent}>{division.trackRecord.kicker}</Kicker>
              <h2 className="mt-4 max-w-2xl font-serif text-2xl font-semibold tracking-tight text-slate-100 sm:text-3xl lg:text-4xl">
                {division.trackRecord.title}
              </h2>
            </Reveal>
            <div className="mt-14 space-y-14">
              {division.trackRecord.groups.map((group, gi) => (
                <Reveal key={group.label} delay={gi * 0.12}>
                  <p
                    className="font-mono text-[11px] uppercase tracking-[0.28em]"
                    style={{ color: division.accent }}
                  >
                    {group.label}
                  </p>
                  <p className="mt-5 font-serif text-3xl font-semibold italic leading-snug text-slate-200 sm:text-4xl lg:text-5xl">
                    {group.items.map((item, i) => (
                      <span key={item}>
                        <span className="transition-colors duration-300 hover:text-gold-light">
                          {item}
                        </span>
                        {i < group.items.length - 1 && (
                          <span className="mx-4 not-italic text-slate-600">·</span>
                        )}
                      </span>
                    ))}
                  </p>
                </Reveal>
              ))}
            </div>
          </div>
        </section>
      )}

      {division.clients && (
        <section className="mx-auto max-w-7xl px-4 pb-20 sm:px-6 lg:px-8 lg:pb-28" data-testid={`${division.slug}-clients`}>
          <Reveal>
            <Kicker color={division.accent}>{division.clients.kicker}</Kicker>
            <h2 className="mt-4 max-w-2xl font-serif text-2xl font-semibold tracking-tight text-slate-100 sm:text-3xl lg:text-4xl">
              {division.clients.title}
            </h2>
          </Reveal>
          <div className="relative mt-12 overflow-hidden">
            <div className="pointer-events-none absolute inset-y-0 left-0 z-10 w-24 bg-gradient-to-r from-void to-transparent" />
            <div className="pointer-events-none absolute inset-y-0 right-0 z-10 w-24 bg-gradient-to-l from-void to-transparent" />
            <div className="flex w-max animate-marquee">
              {[0, 1].map((half) => (
                <div key={half} className="flex items-center">
                  {division.clients.items.map((client) => (
                    <div
                      key={`${half}-${client.name}`}
                      className="group flex items-center gap-4 px-8"
                      data-testid={`${division.slug}-client-${client.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                    >
                      <div className="flex h-16 w-16 shrink-0 items-center justify-center rounded-full bg-slate-100/95 p-2.5 transition-transform duration-500 group-hover:scale-110">
                        <img
                          src={client.logo}
                          alt={client.name}
                          loading="lazy"
                          className="h-10 w-10 object-contain opacity-75 grayscale transition-all duration-500 group-hover:opacity-100 group-hover:grayscale-0"
                        />
                      </div>
                      <div className="whitespace-nowrap">
                        <p className="text-sm font-medium text-slate-200">{client.name}</p>
                        <p className="mt-0.5 font-mono text-[9px] uppercase tracking-[0.22em] text-slate-500">
                          {client.country}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      <section className="mx-auto max-w-7xl px-4 pb-20 sm:px-6 lg:px-8 lg:pb-28" data-testid={`${division.slug}-gallery`}>
        <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
          <Reveal>
            <Kicker color={division.accent}>Galería</Kicker>
            <h2 className="mt-4 font-serif text-2xl font-semibold tracking-tight text-slate-100 sm:text-3xl lg:text-4xl">
              {division.galleryTitle}
            </h2>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="max-w-sm text-sm font-light text-slate-400">{division.galleryIntro}</p>
          </Reveal>
        </div>

        <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {division.images.map((img, i) => (
            <Reveal key={img.url} delay={i * 0.08}>
              <figure className="group">
                <div className="clip-frame relative aspect-[4/3] overflow-hidden bg-ink">
                  <img
                    src={img.url}
                    alt={img.title}
                    loading="lazy"
                    className="h-full w-full object-cover brightness-[0.8] saturate-[0.85] transition-all duration-700 ease-out group-hover:scale-105 group-hover:brightness-100 group-hover:saturate-100"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-void/70 via-void/25 to-void/10 transition-opacity duration-500 group-hover:opacity-30" />
                </div>
                <figcaption className="mt-4">
                  <p
                    className="font-mono text-[10px] uppercase tracking-[0.24em]"
                    style={{ color: division.accent }}
                  >
                    {img.category}
                  </p>
                  <p className="mt-1 text-sm text-slate-300">{img.title}</p>
                </figcaption>
              </figure>
            </Reveal>
          ))}
        </div>
      </section>

      <Marquee />

      <section className="mx-auto max-w-7xl px-4 py-24 text-center sm:px-6 lg:px-8 lg:py-32">
        <Reveal>
          <Kicker color={division.accent} className="justify-center">
            Hablemos
          </Kicker>
          <h2 className="mx-auto mt-6 max-w-2xl font-serif text-3xl font-semibold leading-tight text-slate-100 sm:text-4xl lg:text-5xl">
            {division.tagline}
          </h2>
          <button
            onClick={() => setOpen(true)}
            data-testid={division.form.buttonTestId}
            className="group mt-10 inline-flex items-center gap-4 px-10 py-5 font-mono text-xs uppercase tracking-[0.24em] text-void transition-transform duration-300 hover:-translate-y-1"
            style={{ backgroundColor: division.accent, boxShadow: `0 0 60px -12px ${division.accent}` }}
          >
            {division.cta}
            <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1.5" />
          </button>
        </Reveal>
      </section>

      <InquiryModal open={open} onOpenChange={setOpen} division={division} />
    </main>
  );
}
